import { useMutation } from '@apollo/client'
import { zodResolver } from '@hookform/resolvers/zod'
import { Globe, Loader, Rocket, Split } from 'lucide-react'
import { useMemo, useTransition } from 'react'
import { useForm } from 'react-hook-form'
import { useSearchParams } from 'react-router'
import { toast } from 'sonner'
import { starWars, uniqueNamesGenerator } from 'unique-names-generator'
import { z } from 'zod/v4'
import {
  COMPANY_LEVEL_VALUE,
  ProjectSelectField,
} from '@/components/form/project-select-field'
import { ToasterContent } from '@/components/sv/toaster-content'
import { Muted, H3 } from '@/components/sv/typographies'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  useCompanyPath,
  useCreateFormProjectSelection,
  useIdCompany,
  useMixpanel,
  useRBAC,
  useSuspenseQuery,
} from '@/lib/hooks'
import { cn } from '@/lib/utils'
import { Overview } from '@/routes/load-balancers/create-load-balancer/form/sections/overview'
import {
  CREATE_LOAD_BALANCER,
  CREATE_LOAD_BALANCER_QUERY,
} from '@/routes/load-balancers/create-load-balancer/gql'
import type {
  CreateLoadBalancerMutation,
  CreateLoadBalancerMutationVariables,
  CreateLoadBalancerPageQuery,
  CreateLoadBalancerPageQueryVariables,
} from '@/routes/load-balancers/create-load-balancer/gql.types.gen'

const schema = z.object({
  displayName: z
    .string()
    .trim()
    .min(1, { message: 'Required field' })
    .max(100, { message: 'Name cannot be longer than 100 characters' }),
  type: z.enum(['GEO', 'DEFAULT']),
  idProject: z.string().optional(),
})

export const CreateLoadBalancerForm = () => {
  const { idCompany } = useIdCompany()
  const { rbac, totalServices, rolesInCompany } = useRBAC()
  const { navigateToPath } = useCompanyPath()
  const { track } = useMixpanel()
  const [isPending, startTransition] = useTransition()
  const [searchParams] = useSearchParams()

  const idProjectFromUrl = searchParams.get('idProject')

  const { data } = useSuspenseQuery<
    CreateLoadBalancerPageQuery,
    CreateLoadBalancerPageQueryVariables
  >(CREATE_LOAD_BALANCER_QUERY, {
    variables: { idCompany },
  })

  const [createLoadBalancer] = useMutation<
    CreateLoadBalancerMutation,
    CreateLoadBalancerMutationVariables
  >(CREATE_LOAD_BALANCER)

  const projects = useMemo(() => {
    if (totalServices.totalProjects === 0) {
      return []
    }
    return (data?.company?.projects?.items ?? []).filter((project) =>
      rbac.company(idCompany).project(project.id).loadBalancers.create.can()
    )
  }, [
    data?.company?.projects?.items,
    idCompany,
    rbac,
    totalServices.totalProjects,
  ])

  const shouldShowProjectField = projects.length > 0

  const isCompanyLevelUser = rbac.company(idCompany).loadBalancers.create.can()
  const { initialIdProject, isProjectOptional, schemaWithProjectRequirement } =
    useCreateFormProjectSelection({
      schema,
      rolesInCompany,
      isCompanyLevelUser,
      idProjectFromUrl,
      projects,
    })

  const form = useForm<z.input<typeof schema>>({
    resolver: zodResolver(schemaWithProjectRequirement),
    defaultValues: {
      displayName: uniqueNamesGenerator({
        dictionaries: [starWars],
        separator: ' ',
        length: 1,
      }),
      type: 'DEFAULT',
      idProject: initialIdProject,
    },
  })

  const { trigger } = form

  const onSubmit = async (values: z.infer<typeof schema>) => {
    const isValid = await trigger()
    if (isPending || !isValid) {
      return
    }

    startTransition(async () => {
      try {
        const idProject =
          values.idProject === COMPANY_LEVEL_VALUE
            ? undefined
            : values.idProject

        const res = await createLoadBalancer({
          variables: {
            idCompany,
            idProject,
            displayName: values.displayName,
            type: values.type,
            destinations: [],
          },
        })
        toast(
          <ToasterContent variant="success">
            Load balancer created successfully
          </ToasterContent>,
          { position: 'top-right' }
        )
        track('Load Balancer Created', {
          idLoadBalancer: res.data?.createEdgeLoadBalancer?.id,
          type: values.type.toLowerCase(),
        })

        navigateToPath({
          pathname: '/load-balancer/:loadBalancerName/overview',
          variables: {
            loadBalancerName: res.data?.createEdgeLoadBalancer?.name ?? '',
          },
        })
      } catch {
        toast(
          <ToasterContent variant="error">
            Failed to create load balancer
          </ToasterContent>,
          { position: 'top-right' }
        )
      }
    })
  }

  return (
    <Form {...form}>
      <div className="grid gap-4 grid-cols-1 lg:grid-cols-[2fr_1fr] pb-25">
        <div className="flex flex-col">
          <Card>
            <CardContent className="p-0">
              <H3 className="mb-6 px-6">Load balancer details</H3>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-6 px-6"
              >
                <FormField
                  control={form.control}
                  name="type"
                  render={({ field: { value, onChange } }) => (
                    <FormItem>
                      <FormControl>
                        <RadioGroup
                          className="grid grid-cols-2 gap-4"
                          onValueChange={(value) => onChange(value)}
                          value={value}
                        >
                          <Label
                            className={cn(
                              'relative has-[[data-state=checked]]:border-ring has-[[data-state=checked]]:bg-input/20',
                              'flex flex-col gap-2 items-center rounded-lg border px-3 py-5 cursor-pointer'
                            )}
                          >
                            <RadioGroupItem
                              value="DEFAULT"
                              id="DEFAULT"
                              className="absolute top-3 left-3 data-[state=checked]:border-primary"
                            />
                            <div className="flex flex-col items-center gap-2">
                              <Split className="size-5" />
                              <div className="font-medium">Default</div>
                              <Muted className="text-center">
                                Distribute traffic randomly across all
                                destinations.
                              </Muted>
                            </div>
                          </Label>
                          <Label
                            className={cn(
                              'relative has-[[data-state=checked]]:border-ring has-[[data-state=checked]]:bg-input/20',
                              'flex flex-col gap-2 items-center rounded-lg border px-3 py-5 cursor-pointer'
                            )}
                          >
                            <RadioGroupItem
                              value="GEO"
                              id="GEO"
                              className="absolute top-3 left-3 data-[state=checked]:border-primary"
                            />
                            <div className="flex flex-col items-center gap-2">
                              <Globe className="size-5" />
                              <div className="font-medium">Geo</div>
                              <Muted className="text-center">
                                Route traffic to the closest destination.
                              </Muted>
                            </div>
                          </Label>
                        </RadioGroup>
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="displayName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input
                          autoComplete="off"
                          aria-label="Load balancer name"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {shouldShowProjectField ? (
                  <ProjectSelectField
                    projects={projects}
                    isOptional={isProjectOptional}
                  />
                ) : null}
                <Button disabled={isPending}>
                  {isPending ? <Loader className="animate-spin" /> : <Rocket />}
                  Create load balancer
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
        <div className="flex flex-col gap-4">
          <Overview />
        </div>
      </div>
    </Form>
  )
}
